import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import * as jsDiff from 'diff';

import {
	makeStyles
} from '@material-ui/core';

const greenColor = "#3c756c";
const styles = makeStyles(theme => ({
	wrapper: {
		whiteSpace: "pre-line"
	},

	added: {
		color: greenColor,
		backgroundColor: "#e4f0f1"
	},

	removed: {
		color: greenColor,
		textDecoration: "line-through"
	}
}));

/************************************************************************/
/* VERY IMPORTANT
/*    SINCE THIS COMPONENT USES dangerouslySetInnerHTML MAKE SURE THE
/*    PATCH THAT'S SENT IN IS EITHER SAFE AND / OR SANITIZED
/************************************************************************/

// https://github.com/kpdecker/jsdiff
const TYPES = {
	CHARS: "diffChars",
	WORDS: "diffWords",
	WORDS_WITH_SPACE: "diffWordsWithSpace",
	LINES: "diffLines",
	TRIMMED_LINES: "diffTrimmedLines",
	SENTENCES: "diffSentences"
};

function Diff(props) {
	const patchLengthTreshold = 2500;
	const [diff, setDiff] = useState(null);

	const patchGenesis = (patchObjects, hunkSeparator) => {
		let results = patchObjects.map(file => {
			let {hunks} = file,
				pre = [],
				post = [];

			hunks.map(hunk => {
				hunk.lines.map(line => {
					let i = line.slice(0, 1),
						txt = line.slice(1);

					if(i === '-') {
						pre.push(txt);
					} else if(i === '+') {
						post.push(txt);
					} else {
						pre.push(txt);
						post.push(txt);
					}

					return null;
				});

				pre.push(hunkSeparator);
				post.push(hunkSeparator);

				return null;
			});

			return {
				pre: pre.join("\n"),
				post: post.join("\n")
			};
		});

		return results;
	};

	const ourCharOrWordDiff = useCallback((pre, post) => {
		const randHash = () => Math.random().toString(36).substring(2);

		let result,
			hashes = {},
			imgRx = /(<img[^>]*>)/g,
			everything = pre.concat(post);

		/* eslint-disable no-cond-assign */
		// create random words for each unique image in either pre or post
		while(result = imgRx.exec(everything)) {
			if(result && result.length) {
				if(!(result[1] in hashes)) {
					hashes[result[1]] = randHash();
				}
			}
		}
		/* eslint-disable no-cond-assign */

		// replace each img with a random hash word
		Object.keys(hashes).forEach(tag => {
			pre = pre.replace(tag, hashes[tag]);
			post = post.replace(tag, hashes[tag]);
		});

		result = jsDiff[props.diffType](pre, post);

		Object.keys(hashes).forEach(tag => {
			result.forEach(item => {
				item.value = item.value.replace(hashes[tag], tag);
			});
		});

		return result;
	}, [props.diffType]);

	useEffect(() => {
		let patchResults,
			{patch, hunkSeparator, diffType} = props;

		if (patch) {
			patchResults = patchGenesis(jsDiff.parsePatch(patch), hunkSeparator)[0];

			if (patch.length > patchLengthTreshold) {
				diffType = TYPES.LINES;
			}

			// to fix multiple image and spaces issues
			if (diffType === TYPES.CHARS || diffType === TYPES.WORDS || diffType === TYPES.WORDS_WITH_SPACE) {
				setDiff(ourCharOrWordDiff(patchResults.pre, patchResults.post));
			} else {
				setDiff(jsDiff[diffType](patchResults.pre, patchResults.post));
			}
		}
	}, [props, setDiff, ourCharOrWordDiff]);

	let color,
		classes = styles(),
		{wordBreak, patch} = props;

	return patch ?
		<div className={classes.wrapper} style={wordBreak ? {wordBreak: wordBreak} : null}>
			{diff ? diff.map((part, idx) => {
				// normal color for common parts
				// green for additions, red for deletions
				color = part.added ? classes.added : part.removed ? classes.removed : null;
				return <span key={idx} className={color} dangerouslySetInnerHTML={{__html: part.value}}></span>;
			}) : null}
		</div>: null;
}

Diff.defaultProps = {
	diffType: TYPES.WORDS_WITH_SPACE,
	hunkSeparator: Array(45).fill("&#183; ").join("")
};

Diff.propTypes = {
	patch: PropTypes.string,
	hunkSeparator: PropTypes.string,
	diffType: PropTypes.string,
	wordBreak: PropTypes.string
};

export default Diff;